import { CheckCircle2, Loader2 } from "lucide-react";
import { useState } from "react";

import { useCreateOrganizationTunnel, useOrganizationTunnels } from "@/client/backend/tunnels";
import { FormError, SubmitButton } from "@/components/network/form";
import { SelectorsInput, selectorLines } from "@/components/network/selectors-input";
import { ConnectorCommand } from "@/components/network/connector-command";
import { TokenExpirationField, tokenFromForm } from "@/components/network/tunnel-token";
import { CopyButton, CopyableCommand } from "@/components/copy";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// The same rule the backend applies to a tunnel name, which instances use in
// spec.tunnel.
const tunnelName = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;

// Created hands over the connector command, which carries the tunnel's
// token, and waits live for the connector to come up, as Cloudflare does.
function Created({ organizationId, tunnelId, name, tokenValue }: { organizationId: string; tunnelId: string; name: string; tokenValue: string }) {
	// Poll briskly until the connector arrives, then stop.
	const find = (list?: { data?: { tunnels: { id: string; online: boolean }[] } }) =>
		list?.data?.tunnels.find((t) => t.id === tunnelId);
	const { data } = useOrganizationTunnels(organizationId, (latest) => (find(latest)?.online ? 0 : 3_000));
	const tunnel = data?.data?.tunnels.find((t) => t.id === tunnelId);
	return (
		<div className="min-w-0 space-y-4">
			<ConnectorCommand tokenValue={tokenValue} />
			<div role="status" className="flex items-center gap-2 rounded-md border p-3 text-sm">
				{tunnel?.online ? (
					<>
						<CheckCircle2 aria-hidden className="size-4 text-success" />
						Connected{tunnel.hostname ? ` from ${tunnel.hostname}` : ""}
					</>
				) : (
					<>
						<Loader2 aria-hidden className="size-4 animate-spin text-muted-foreground" />
						Waiting for the connector...
					</>
				)}
			</div>
			{tunnel?.online && (
				<div className="space-y-2">
					<Label>Instances use it when they name it</Label>
					<CopyableCommand command={`lim ios create --tunnel ${name}`} label="Copy the create command" />
				</div>
			)}
		</div>
	);
}

export function NewTunnelDialog({
	organizationId,
	open,
	onOpenChange,
}: {
	organizationId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [created, setCreated] = useState<{ id: string; name: string; token: string }>();
	const [error, setError] = useState<string>();
	const { trigger: createTunnel, isMutating } = useCreateOrganizationTunnel(organizationId);

	async function submit(form: HTMLFormElement) {
		const data = new FormData(form);
		const name = String(data.get("name") ?? "").trim();
		const selectors = selectorLines(String(data.get("selectors") ?? ""));
		if (!tunnelName.test(name) || name.length > 63) {
			setError("Use lowercase letters, digits and dashes, at most 63 characters, starting and ending with a letter or digit.");
			return;
		}
		if (selectors.length === 0) {
			setError("Add at least one selector.");
			return;
		}
		setError(undefined);
		const response = await createTunnel({ name, selectors, token: tokenFromForm(data) });
		if (!response || response.error || !response.data.token) {
			setError(response?.error?.message ?? "The tunnel could not be created.");
			return;
		}
		setCreated({ id: response.data.tunnel.id, name, token: response.data.token.token });
	}

	// Done closes through here too: Radix reports only its own closes, so
	// the next open would otherwise show this token again.
	function changeOpen(next: boolean) {
		onOpenChange(next);
		if (!next) {
			setCreated(undefined);
			setError(undefined);
		}
	}

	return (
		<Dialog open={open} onOpenChange={changeOpen}>
			{/* The token is shown once, so a click outside never dismisses it. */}
			<DialogContent
				className="sm:max-w-lg"
				onInteractOutside={(event) => {
					if (created) event.preventDefault();
				}}
			>
				<DialogHeader>
					<DialogTitle>{created ? `Run ${created.name}` : "New tunnel"}</DialogTitle>
					<DialogDescription>
						{created
							? "Run this on a machine that can reach your services."
							: "A tunnel runs on a machine in your network and serves every instance that names it."}
					</DialogDescription>
				</DialogHeader>
				{created ? (
					<>
						<Created organizationId={organizationId} tunnelId={created.id} name={created.name} tokenValue={created.token} />
						<DialogFooter>
							<CopyButton value={created.token} label="Copy the tunnel token" text="Copy token" />
							<Button onClick={() => changeOpen(false)}>Done</Button>
						</DialogFooter>
					</>
				) : (
					<form
						className="min-w-0 space-y-4"
						onSubmit={(event) => {
							event.preventDefault();
							void submit(event.currentTarget);
						}}
					>
						<div className="space-y-2">
							<Label htmlFor="tunnel-name">Name</Label>
							<Input id="tunnel-name" name="name" placeholder="staging" autoComplete="off" data-1p-ignore disabled={isMutating} />
							<p className="text-xs text-muted-foreground">
								Instances name it with <code>--tunnel</code>. Cannot be changed later.
							</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="tunnel-selectors">Selectors</Label>
							<SelectorsInput
								id="tunnel-selectors"
								rows={3}
								placeholder={"localhost:3000\n*.internal.example.com"}
								disabled={isMutating}
							/>
						</div>
						<div className="sm:w-40">
							<TokenExpirationField idPrefix="tunnel-token" disabled={isMutating} />
						</div>
						<FormError error={error} />
						<DialogFooter>
							<SubmitButton pending={isMutating}>Create tunnel</SubmitButton>
						</DialogFooter>
					</form>
				)}
			</DialogContent>
		</Dialog>
	);
}
